

import React, { useState, useEffect } from 'react';
import { User, UserRole } from '../types';
import { getUsers, getRoles, createUser, updateUser, deleteUser, getPermissions, createRole, updateRole } from '../services/apiService';
import { LoadingSpinner } from './icons';
import UserModal from './UserModal';
import RoleModal from './RoleModal';
import ConfirmDeleteModal from './ConfirmDeleteModal';

type Tab = 'users' | 'roles';

interface UsersAndRolesPageProps {
    user: User;
    onUserUpdate: (user: User) => void;
}

const UserCard: React.FC<{
    user: User, 
    currentUser: User,
    onEdit: () => void, 
    onDelete: () => void
}> = ({ user, currentUser, onEdit, onDelete }) => (
    <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
        <div className="flex justify-between items-start">
            <div>
                <p className="font-bold text-gray-900">{user.name}</p>
                <p className="text-sm text-gray-500">{user.email}</p>
                 <p className="text-sm text-gray-500">{user.organizationName}</p>
            </div>
             <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {user.isActive ? 'Active' : 'Inactive'}
            </span>
        </div>
        <div className="mt-4 pt-4 border-t flex justify-between items-center">
             <span className="text-sm font-medium text-gray-700">{user.role}</span>
             <div className="space-x-4 text-sm font-medium">
                <button onClick={onEdit} className="text-primary-light hover:text-primary-dark">Edit</button>
                <button onClick={onDelete} className="text-red-600 hover:text-red-900" disabled={user.id === currentUser.id}>Delete</button>
             </div>
        </div>
    </div>
);


const UsersAndRolesPage: React.FC<UsersAndRolesPageProps> = ({ user: currentUser, onUserUpdate }) => {
    const [activeTab, setActiveTab] = useState<Tab>('users');
    const [users, setUsers] = useState<User[]>([]);
    const [roles, setRoles] = useState<any[]>([]);
    const [allPermissions, setAllPermissions] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    
    const [isUserModalOpen, setIsUserModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [userToDelete, setUserToDelete] = useState<User | null>(null);
    
    const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
    const [selectedRole, setSelectedRole] = useState<any | null>(null);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const [usersData, rolesData, permissionsData] = await Promise.all([getUsers(), getRoles(), getPermissions()]);
            setUsers(usersData);
            setRoles(rolesData);
            setAllPermissions(permissionsData);
        } catch (err) {
            setError('Failed to fetch data.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);
    
    const openUserModalForEdit = (user: User) => {
        setSelectedUser(user);
        setIsUserModalOpen(true);
    };

    const openUserModalForAdd = () => {
        setSelectedUser(null);
        setIsUserModalOpen(true);
    };

    const handleSaveUser = async (data: Partial<User> & { password?: string }, userId: string | null) => {
        if (userId) {
            const updatedUser = await updateUser(userId, data, currentUser);
            if (updatedUser.id === currentUser.id) {
                onUserUpdate(updatedUser);
            }
        } else {
            await createUser(data, currentUser);
        }
        await fetchData();
    };
    
    const handleDeleteClick = (user: User) => {
        if (user.role === UserRole.SUPER_ADMIN && currentUser.role !== UserRole.SUPER_ADMIN) {
            alert("You do not have permission to delete a Super Admin.");
            return;
        }
        setUserToDelete(user);
    };

    const handleConfirmDelete = async () => {
        if (!userToDelete) return;

        try {
            await deleteUser(userToDelete.id, currentUser);
            setUserToDelete(null); // Close modal on success
            await fetchData();
        } catch (err) {
            // Re-throw so the modal can display the error
            throw err;
        }
    };

    const openRoleModalForEdit = (role: any) => {
        setSelectedRole(role);
        setIsRoleModalOpen(true);
    };

    const openRoleModalForAdd = () => {
        setSelectedRole(null);
        setIsRoleModalOpen(true);
    };
    
    const handleSaveRole = async (data: any, roleId: string | null) => {
        if (roleId) {
            await updateRole(roleId, data, currentUser);
        } else {
            await createRole(data, currentUser);
        }
        await fetchData();
    };


    const renderUsersTab = () => (
        <div>
             <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Manage Users</h2>
                <button
                    onClick={openUserModalForAdd}
                    className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-light w-full sm:w-auto"
                >
                    Create User
                </button>
            </div>
             {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Organization</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {users.map(u => (
                            <tr key={u.id}>
                                <td className="px-6 py-4">
                                    <div className="text-sm font-medium text-gray-900">{u.name}</div>
                                    <div className="text-sm text-gray-500">{u.email}</div>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-700">{u.organizationName}</td>
                                <td className="px-6 py-4 text-sm text-gray-700">{u.role}</td>
                                <td className="px-6 py-4">
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${u.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                        {u.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                                <td className="px-6 py-4 text-sm font-medium space-x-4">
                                    <button onClick={() => openUserModalForEdit(u)} className="text-primary-light hover:text-primary-dark">Edit</button>
                                    <button onClick={() => handleDeleteClick(u)} className="text-red-600 hover:text-red-900" disabled={u.id === currentUser.id}>Delete</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {/* Mobile Cards */}
            <div className="md:hidden space-y-3">
                {users.map(u => (
                    <UserCard
                        key={u.id}
                        user={u}
                        currentUser={currentUser}
                        onEdit={() => openUserModalForEdit(u)}
                        onDelete={() => handleDeleteClick(u)}
                    />
                ))}
            </div>
        </div>
    );
    
    const renderRolesTab = () => (
         <div>
            <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 mb-6">
                 <h2 className="text-2xl font-bold text-gray-800">Platform Roles & Permissions</h2>
                  <button
                    onClick={openRoleModalForAdd}
                    className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-light w-full sm:w-auto"
                >
                    Create Role
                </button>
            </div>
            <div className="space-y-4">
            {roles.map(role => (
                <div key={role.id} className="p-4 border rounded-lg flex flex-col sm:flex-row justify-between sm:items-start">
                    <div className="flex-grow">
                        <h3 className="font-bold text-lg text-primary-dark">{role.name}</h3>
                        <p className="text-gray-600 text-sm">{role.description}</p>
                        <details className="mt-2 text-sm">
                            <summary className="cursor-pointer font-medium text-gray-700">View Permissions ({role.permissions.length})</summary>
                            <ul className="list-disc list-inside text-gray-500 mt-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4">
                                {role.permissions.map((p: string) => <li key={p}>{p.replace(/_/g, ' ')}</li>)}
                            </ul>
                        </details>
                    </div>
                     <button onClick={() => openRoleModalForEdit(role)} className="text-primary-light hover:text-primary-dark text-sm font-medium mt-2 sm:mt-0 flex-shrink-0">Edit</button>
                </div>
            ))}
            </div>
        </div>
    );

    return (
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
            <h1 className="text-3xl font-bold text-gray-800 mb-6">Users and Roles</h1>
            <div className="border-b border-gray-200 overflow-x-auto">
                <nav className="-mb-px flex space-x-4 sm:space-x-8 whitespace-nowrap" aria-label="Tabs">
                    <button onClick={() => setActiveTab('users')} className={`${activeTab === 'users' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}>
                        Users
                    </button>
                    <button onClick={() => setActiveTab('roles')} className={`${activeTab === 'roles' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}>
                        Roles & Permissions
                    </button>
                </nav>
            </div>
            
            <div className="mt-6">
                {loading && <div className="flex justify-center items-center py-8"><LoadingSpinner className="w-8 h-8 text-primary" /></div>}
                {error && <div className="text-red-500 p-4 bg-red-100 rounded-md">{error}</div>}
                {!loading && !error && (
                    activeTab === 'users' ? renderUsersTab() : renderRolesTab()
                )}
            </div>

            {isUserModalOpen && (
                <UserModal
                    user={selectedUser}
                    currentUser={currentUser}
                    onClose={() => setIsUserModalOpen(false)}
                    onSave={handleSaveUser}
                />
            )}
            
            {isRoleModalOpen && (
                <RoleModal
                    role={selectedRole}
                    allPermissions={allPermissions}
                    onClose={() => setIsRoleModalOpen(false)}
                    onSave={handleSaveRole}
                />
            )}

            {userToDelete && (
                <ConfirmDeleteModal
                    isOpen={!!userToDelete}
                    onClose={() => setUserToDelete(null)}
                    onConfirm={handleConfirmDelete}
                    resourceName={`${userToDelete.name} (${userToDelete.email})`}
                    confirmText="DELETE"
                />
            )}
        </div>
    );
};

export default UsersAndRolesPage;
